import { useRef, useEffect, useCallback, memo, useMemo } from "react";

import Flatpickr from "react-flatpickr";
import "flatpickr/dist/themes/light.css";
import { TimeIcon } from "components/Icons";
import FormField from "./FormField";
import { cn, colors } from "tools/ui";

function toDateObject(value) {
  if (!value) return null;
  if (value instanceof Date) {
    return Number.isNaN(value.getTime()) ? null : value;
  }
  if (typeof value === "string" && value.includes("T")) {
    const timePart = value.split("T")[1];
    const [h, m, s] = timePart.replace(/Z$/, "").split(":").map(Number);
    const d = new Date();
    d.setHours(h, m, s || 0, 0);
    return d;
  }
  if (typeof value === "string" && /^\d{2}:\d{2}(:\d{2})?$/.test(value)) {
    const [h, m, s] = value.split(":").map(Number);
    if (h > 23 || m > 59 || (s !== undefined && s > 59)) return null;
    const d = new Date();
    d.setHours(h, m || 0, s || 0, 0);
    return d;
  }
  return null;
}

const MemoFlatpickr = memo(function MemoFlatpickr({
  fp,
  options,
  initialValue,
  onChange,
  onClick,
  disabled,
  placeholder,
  className,
}) {
  return (
    <Flatpickr
      ref={fp}
      options={options}
      defaultValue={initialValue ? undefined : undefined} // uncontrolled
      onChange={onChange}
      onClick={onClick}
      disabled={disabled}
      placeholder={placeholder}
      className={className}
    />
  );
});

function TimePicker({
  name,
  label,
  onChange,
  field,
  data,
  readOnly,
  placeholder,
  colSpan,
  model,
}) {
  const fp = useRef(null);
  const dataRef = useRef(data);
  const onChangeRef = useRef(onChange);
  const nameRef = useRef(name);
  const initialValueRef = useRef(toDateObject(data?.value ?? null));

  const options = useMemo(
    () => ({
      enableTime: true,
      noCalendar: true,
      dateFormat: "H:i",
      disableMobile: true,
    }),
    [],
  );

  dataRef.current = data;
  onChangeRef.current = onChange;
  nameRef.current = name;

  const handleTimeChange = useCallback((selectedDates) => {
    if (selectedDates.length === 0) return;

    const date = selectedDates[0];

    const hh = String(date.getHours()).padStart(2, "0");
    const mm = String(date.getMinutes()).padStart(2, "0");
    const ss = String(date.getSeconds()).padStart(2, "0");

    const localTime = `${hh}:${mm}:${ss}`;
    if (dataRef.current) dataRef.current.value = localTime;
    if (onChangeRef.current) onChangeRef.current(nameRef.current, localTime);
  }, []);

  const openTimePicker = useCallback(() => {
    if (readOnly || !fp.current) return;
    fp.current.flatpickr.open();
  }, [readOnly]);

  useEffect(() => {
    if (!fp.current?.flatpickr) return;
    if (initialValueRef.current) {
      fp.current.flatpickr.setDate(initialValueRef.current, false);
    }
  }, []);

  useEffect(() => {
    if (!fp.current?.flatpickr) return;
    if (data?.value) {
      const d = toDateObject(data.value);
      if (d) {
        fp.current.flatpickr.setDate(d, false);
      } else {
        fp.current.flatpickr.clear();
      }
    } else {
      fp.current.flatpickr.clear();
    }
  }, [data?.value]);

  const _color = readOnly ? colors.readOnly : "bg-white";

  return (
    <FormField
      label={label}
      name={name}
      required={field?.required}
      colspan={colSpan}
      model={model}
    >
      <div
        className={cn(
          "flex items-center border border-slate-300 rounded-md focus-within:border-sky-400 h-10",
          _color,
        )}
      >
        <MemoFlatpickr
          fp={fp}
          options={options}
          onChange={handleTimeChange}
          onClick={openTimePicker}
          disabled={readOnly}
          placeholder={placeholder || "Seleccionar hora"}
          className={cn(
            "outline-none w-full py-2 px-2 rounded-md wrap bg-transparent",
            readOnly ? "cursor-not-allowed" : "cursor-pointer",
          )}
        />
        <TimeIcon
          onClick={openTimePicker}
          className={cn(
            "text-start mr-2 h-6 w-6 my-auto text-neutral-500 cursor-pointer",
            readOnly && "cursor-not-allowed",
          )}
          aria-hidden="true"
          disabled={readOnly}
        />
      </div>
    </FormField>
  );
}

export default TimePicker;
